import { createRouter, createWebHistory } from 'vue-router'
import OverviewPage from '@/views/OverviewPage.vue'
import SystemPage from '@/views/SystemPage.vue'
import DemoLayout from '@/views/demo/DemoLayout.vue'
import DemoLandingPage from '@/views/demo/DemoLandingPage.vue'
import DemoWorkspaceDetailPage from '@/views/demo/DemoWorkspaceDetailPage.vue'
import DemoJobDetailPage from '@/views/demo/DemoJobDetailPage.vue'
import LoginPage from '@/views/LoginPage.vue'
import ConsoleLayout from '@/views/console/ConsoleLayout.vue'
import ConsoleOverview from '@/views/console/ConsoleOverview.vue'
import ConsolePlans from '@/views/console/ConsolePlans.vue'
import ConsolePlanDetail from '@/views/console/ConsolePlanDetail.vue'
import ConsoleWorkspaces from '@/views/console/ConsoleWorkspaces.vue'
import ConsoleWorkspaceCreate from '@/views/console/ConsoleWorkspaceCreate.vue'
import ConsoleWorkspaceDetail from '@/views/console/ConsoleWorkspaceDetail.vue'
import ConsoleAnalytics from '@/views/console/ConsoleAnalytics.vue'
import ConsoleSettings from '@/views/console/ConsoleSettings.vue'
import ConsoleJobs from '@/views/console/ConsoleJobs.vue'
import ConsoleJobCreate from '@/views/console/ConsoleJobCreate.vue'
import ConsoleJobDetail from '@/views/console/ConsoleJobDetail.vue'
import ToolsLayout from '@/views/tools/ToolsLayout.vue'
import ToolsIndex from '@/views/tools/ToolsIndex.vue'
import JobDescriptionPromptPage from '@/views/tools/JobDescriptionPromptPage.vue'
import StandardsPage from '@/views/tools/StandardsPage.vue'
import KnowledgeBasePage from '@/views/tools/KnowledgeBasePage.vue'
import { useAuth } from '@/composables/useAuth.js'
import { appFrontendLogger } from '@/services/FrontendLogger'
import { appFrontendExceptionReporter } from '@/services/FrontendExceptionReporter'

const logger = appFrontendLogger.child('[router]')
const exceptionReporter = appFrontendExceptionReporter.child('router')

const routes = [
  {
    path: '/',
    name: 'overview',
    component: OverviewPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/system',
    name: 'system',
    component: SystemPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/demo',
    component: DemoLayout,
    meta: { requiresAuth: false },
    children: [
      {
        path: '',
        name: 'demo',
        component: DemoLandingPage,
      },
      {
        path: 'workspaces/:id',
        name: 'demo-workspace-detail',
        component: DemoWorkspaceDetailPage,
      },
      {
        path: 'jobs/:id',
        name: 'demo-job-detail',
        component: DemoJobDetailPage,
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresAuth: false },
  },
  // Redirects for removed docs routes
  {
    path: '/docs/getting-started',
    redirect: '/',
    meta: { requiresAuth: false },
  },
  {
    path: '/docs',
    redirect: '/',
    meta: { requiresAuth: false },
  },
  {
    path: '/console',
    component: ConsoleLayout,
    // No meta.requiresAuth = requires auth by default
    children: [
      {
        path: '',
        name: 'ConsoleOverview',
        component: ConsoleOverview,
      },
      {
        path: 'plans',
        name: 'ConsolePlans',
        component: ConsolePlans,
      },
      {
        path: 'plans/:id',
        name: 'ConsolePlanDetail',
        component: ConsolePlanDetail,
      },
      {
        path: 'workspaces',
        name: 'ConsoleWorkspaces',
        component: ConsoleWorkspaces,
      },
      {
        path: 'workspaces/create',
        name: 'ConsoleWorkspaceCreate',
        component: ConsoleWorkspaceCreate,
      },
      {
        path: 'workspaces/:id',
        name: 'ConsoleWorkspaceDetail',
        component: ConsoleWorkspaceDetail,
      },
      {
        path: 'jobs',
        name: 'ConsoleJobs',
        component: ConsoleJobs,
      },
      {
        path: 'jobs/create',
        name: 'ConsoleJobCreate',
        component: ConsoleJobCreate,
      },
      {
        path: 'jobs/:id',
        name: 'ConsoleJobDetail',
        component: ConsoleJobDetail,
      },
      {
        path: 'analytics',
        name: 'ConsoleAnalytics',
        component: ConsoleAnalytics,
      },
      {
        path: 'settings',
        name: 'ConsoleSettings',
        component: ConsoleSettings,
      },
      // The left-nav "Runs" label maps to /console/jobs; /console/runs is a
      // natural guess that would otherwise fall through to the marketing site.
      // Alias it to the runs list, preserving any status/workspace query. (#716)
      {
        path: 'runs',
        redirect: (to) => ({ name: 'ConsoleJobs', query: to.query }),
      },
      // Unknown /console/* paths stay inside the console shell instead of
      // falling through to the global catch-all, which ejects the operator to
      // the marketing landing page. Keep them in the console at the overview. (#716)
      {
        path: ':pathMatch(.*)*',
        redirect: '/console',
      },
    ],
  },
  // Tools — public, unguarded, lives under /console/ URL but is NOT a child of the
  // authenticated /console route (top-level peer). This prevents ConsoleLayout's
  // authenticated plan-count refresh from running for unauthenticated visitors and
  // keeps the auth meta unambiguous. Issue #256 follow-up.
  {
    path: '/console/tools',
    component: ToolsLayout,
    meta: { requiresAuth: false },
    children: [
      {
        path: '',
        name: 'ToolsIndex',
        component: ToolsIndex,
      },
      {
        path: 'job-description-prompt',
        name: 'ToolJobDescriptionPrompt',
        component: JobDescriptionPromptPage,
      },
      {
        path: 'standards',
        name: 'ToolStandards',
        component: StandardsPage,
      },
      {
        path: 'knowledge-base',
        name: 'ToolKnowledgeBase',
        component: KnowledgeBasePage,
      },
      // Unknown /console/tools/* sub-paths must stay PUBLIC. Without this, they
      // fall through to the authenticated /console child catch-all above, which
      // redirects into the guarded console shell and bounces anonymous visitors
      // to /login — a login wall on a deliberately public surface. This wildcard
      // resolves to /console/tools/:pathMatch(.*)* which is MORE specific than
      // /console/:pathMatch(.*)*, so Vue Router ranks it first, and it inherits
      // meta.requiresAuth: false from this route. Redirect to the public tools
      // index. (#723)
      {
        path: ':pathMatch(.*)*',
        redirect: '/console/tools',
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Auth guard — redirect unauthenticated users to /login
router.beforeEach((to) => {
  const { isAuthenticated } = useAuth()

  // If route explicitly opts out of auth, allow it
  if (to.meta.requiresAuth === false) {
    // But redirect authenticated users away from login page
    if (to.name === 'login' && isAuthenticated.value) {
      return { path: '/console' }
    }
    return true
  }

  // Default: all routes require auth
  if (!isAuthenticated.value) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  return true
})

// Handle chunk loading errors after deployments
// When a new version is deployed, old chunks may no longer exist
router.onError((error, to) => {
  // Check if this is a chunk loading error
  const isChunkError = (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Loading CSS chunk') ||
    error.name === 'ChunkLoadError'
  )

  if (isChunkError) {
    // Store the target path and reload to get fresh chunks
    const targetPath = to?.fullPath || window.location.pathname
    exceptionReporter.capture(error, {
      source: 'router-chunk-error',
      routePath: targetPath,
    })
    logger.warn(`Chunk load error detected, reloading to ${targetPath}`)

    // Use sessionStorage to prevent infinite reload loops
    const reloadKey = 'chunk-reload-' + targetPath
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, 'true')
      // Clear after 10 seconds to allow retry later if needed
      setTimeout(() => sessionStorage.removeItem(reloadKey), 10000)
      window.location.href = targetPath
    }
  }
})

export default router
